/**
 * hooks/useUserForms.ts
 *
 * Shared hook that fetches all form templates visible to the current user:
 * - Admins: admin-created forms (created_by IS NOT NULL) + their own forms.
 * - Regular users: their own forms (is_visible_to_user=true) + forms assigned
 *   to them via form_template_assignments (is_active=true, any status).
 *   Note: is_visible_to_user is NOT checked for assigned templates - only
 *   form_template_assignments.is_active governs visibility for assigned forms.
 * Results are sorted by last_sent_at desc (most recently sent first), with
 * updated_at as fallback for forms never sent. An optional limit caps the count.
 * is_assigned=true for forms the user does not personally own.
 *
 * Race-condition safety: each effect invocation captures a `cancelled` boolean
 * that is flipped to true by the cleanup function on unmount or dependency
 * change. All state setters are guarded by this flag so stale async responses
 * never overwrite newer state. On error, forms are explicitly reset to [].
 *
 * Updated: Sentry breadcrumbs added on each data-access branch so that RLS
 * regressions (e.g. user suddenly unable to see admin forms) are traceable
 * in Sentry replays without waiting for a user report.
 * Updated Jun 2026: uses useEffectiveUser so that in impersonation mode
 * the hook fetches forms for the impersonated user (not the admin).
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveUser } from './useEffectiveUser';
import { supabase } from '../lib/supabase';
import { captureError, addBreadcrumb } from '../utils/errorReporting';
import type { RecentForm } from '../components/dashboard/DashboardFormsOverview';

interface UseUserFormsOptions {
  limit?: number;
}

interface UseUserFormsResult {
  forms: RecentForm[];
  loading: boolean;
}

type RawForm = { id: string; name: string; status: string; updated_at: string; user_id: string };

export function useUserForms({ limit }: UseUserFormsOptions = {}): UseUserFormsResult {
  const { user, userProfile } = useAuth();
  const { effectiveUserId, effectiveIsAdmin } = useEffectiveUser();

  const [forms, setForms] = useState<RecentForm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || userProfile === null || !effectiveUserId) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setForms([]);

      try {
        let ownedForms: RawForm[] = [];
        let assignedForms: RawForm[] = [];

        if (effectiveIsAdmin) {
          addBreadcrumb('Chargement des formulaires (mode admin)', 'data.read', { userId: effectiveUserId });
          const { data, error } = await supabase
            .from('form_templates')
            .select('id, name, status, updated_at, user_id')
            .or(`created_by.not.is.null,user_id.eq.${effectiveUserId}`)
            .order('updated_at', { ascending: false });

          if (error) throw error;
          ownedForms = data || [];
          addBreadcrumb('Formulaires admin chargés', 'data.read', { count: ownedForms.length, userId: effectiveUserId });
        } else {
          addBreadcrumb('Chargement des formulaires (mode utilisateur)', 'data.read', { userId: effectiveUserId });
          const [
            { data: ownData, error: ownError },
            { data: assignData, error: assignError },
          ] = await Promise.all([
            supabase
              .from('form_templates')
              .select('id, name, status, updated_at, user_id')
              .eq('user_id', effectiveUserId)
              .eq('is_visible_to_user', true)
              .order('updated_at', { ascending: false }),
            supabase
              .from('form_template_assignments')
              .select('template_id')
              .eq('user_id', effectiveUserId)
              .eq('is_active', true),
          ]);

          if (ownError) throw ownError;
          if (assignError) throw assignError;

          ownedForms = ownData || [];

          const ownedIds = new Set(ownedForms.map((t) => t.id));
          const newAssignedIds = (assignData || [])
            .map((a) => a.template_id)
            .filter((id) => !ownedIds.has(id));

          addBreadcrumb('Formulaires propres + assignés récupérés', 'data.read', {
            userId: effectiveUserId,
            ownedCount: ownedForms.length,
            assignedIdsCount: newAssignedIds.length,
          });

          if (newAssignedIds.length > 0) {
            const { data: atData, error: atError } = await supabase
              .from('form_templates')
              .select('id, name, status, updated_at, user_id')
              .in('id', newAssignedIds)
              .eq('status', 'active');

            if (atError) throw atError;
            assignedForms = atData || [];
            addBreadcrumb('Formulaires assignés chargés', 'data.read', { count: assignedForms.length, userId: effectiveUserId });
          }
        }

        if (cancelled) return;

        const ownedIds = new Set(ownedForms.map((t) => t.id));
        const allForms = [...ownedForms, ...assignedForms];
        const templateIds = allForms.map((t) => t.id);

        let lastSentMap = new Map<string, string>();
        if (templateIds.length > 0) {
          const { data: sentData, error: sentError } = await supabase
            .from('requests')
            .select('template_id, created_at')
            .in('template_id', templateIds)
            .order('created_at', { ascending: false });

          if (sentError) {
            console.error('[useUserForms] Error fetching last sent dates:', sentError);
          } else {
            (sentData || []).forEach((r) => {
              if (!lastSentMap.has(r.template_id)) {
                lastSentMap.set(r.template_id, r.created_at);
              }
            });
          }
        }

        if (cancelled) return;

        allForms.sort((a, b) => {
          const aSent = lastSentMap.get(a.id);
          const bSent = lastSentMap.get(b.id);
          if (aSent && bSent) return new Date(bSent).getTime() - new Date(aSent).getTime();
          if (aSent) return -1;
          if (bSent) return 1;
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        });

        const result: RecentForm[] = allForms.map((t) => ({
          id: t.id,
          name: t.name,
          status: t.status,
          updated_at: t.updated_at,
          last_sent_at: lastSentMap.get(t.id) ?? null,
          is_assigned: !ownedIds.has(t.id),
        }));

        setForms(limit !== undefined ? result.slice(0, limit) : result);
      } catch (err) {
        if (cancelled) return;
        console.error('[useUserForms] Error fetching forms:', err);
        captureError(err, { hook: 'useUserForms', userId: effectiveUserId });
        setForms([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user, userProfile, effectiveUserId, effectiveIsAdmin, limit]);

  return { forms, loading };
}
